import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { NotifyEventType } from 'enums/app'
import { useMemberDataSource } from 'hooks/dataSource/useMemberDataSource'
import { useTaskKindDataSource } from 'hooks/dataSource/useTaskKindDataSource'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { useAppDispatch } from 'states/hooks'

import {
  AssigneeMap,
  InsertTasksFromTemplateRequest,
  TaskKindMap,
} from '@crew/apis/task/models/insertTasksFromTemplate/request'
import {
  useLazyGetTaskTemplatesQuery,
  useLazyLoadTaskTemplateQuery,
} from '@crew/apis/taskTemplate/taskTemplateApis'
import { LoadTaskTemplateResponse_TaskTemplateTasks } from '@crew/apis/taskTemplate/models/loadTaskTemplate/response'
import { useInsertTasksFromTemplateMutation } from '@crew/apis/task/taskApis'
import { TaskTemplateScope, EntityType } from '@crew/enums/domain'
import { JsonDateFormat } from '@crew/enums/system'
import { Task } from '@crew/models/domain'
import { TaskKindRef, UserRef } from '@crew/models/refs'
import dayjs from '@crew/modules/dayjs'
import { useTranslation } from '@crew/modules/i18n'
import { useEffectOnce } from '@dx-system/react-use'
import {
  notifyTaskEvent,
  ObjectEventMessage,
} from 'features/app/states/appSlice'

export type TaskTemplateListItem = {
  id: string
  name: string
  scope: string
  updatedAt: string
  updatedBy: UserRef
}

export type TemplateTaskViewModel =
  LoadTaskTemplateResponse_TaskTemplateTasks & {
    level: number
  }

export type FormValues = {
  searchKeyword: string
  baseDate: Date
  userMappings: Record<string, string | null>
  taskKindMappings: Record<string, string | null>
}

// タスクの階層レベルを構築
const buildTaskLevels = (
  tasks: LoadTaskTemplateResponse_TaskTemplateTasks[]
): Record<string, number> => {
  const levelMap: Record<string, number> = {}
  const lookup = new Map(
    tasks.map((task) => [task.id, task.parentTaskTemplateTaskId])
  )

  const resolveLevel = (
    taskId: string | null,
    visited: Set<string> = new Set()
  ): number => {
    if (!taskId) {
      return 0
    }
    if (levelMap[taskId] !== undefined) {
      return levelMap[taskId]
    }

    // 同じノードを再訪問した場合はサイクルとみなす
    // タスクテンプレートタスクの親子関係が正しく登録されていれば発生しないはずだが、
    // 無限ループを発生させないため、念のため防御策を入れておく
    if (visited.has(taskId)) {
      // サイクル検出時はレベル0とする
      return 0
    }
    visited.add(taskId)

    const parentId = lookup.get(taskId) ?? null
    const level = resolveLevel(parentId, visited) + 1
    levelMap[taskId] = level
    return level
  }

  tasks.forEach((task) => {
    resolveLevel(task.id)
  })

  return levelMap
}

// テンプレートのタスク表示用データ構築
const buildTaskView = (tasks: LoadTaskTemplateResponse_TaskTemplateTasks[]) => {
  const levelMap = buildTaskLevels(tasks)
  return [...tasks]
    .sort((a, b) => a.taskOrder.localeCompare(b.taskOrder))
    .map<TemplateTaskViewModel>((task) => ({
      ...task,
      level: levelMap[task.id] ?? 0,
    }))
}

export const useTaskEntryByTemplateForm = (
  projectId: string,
  isOpen: boolean,
  onSubmit: () => void
) => {
  const { t } = useTranslation()
  const toast = useToast()
  const [showApiErrors] = useShowApiErrors()
  const dispatch = useAppDispatch()

  // テンプレートからタスク登録API
  const [insertTasksFromTemplateMutation, { isLoading: isRegistering }] =
    useInsertTasksFromTemplateMutation()

  // タスクテンプレート取得API
  const [lazyGetTaskTemplatesQuery] = useLazyGetTaskTemplatesQuery()
  // タスクテンプレートタスク取得API
  const [lazyLoadTaskTemplateQuery] = useLazyLoadTaskTemplateQuery()

  const { control, watch, reset, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      searchKeyword: '',
      baseDate: new Date(),
      userMappings: {},
      taskKindMappings: {},
    },
  })

  const baseDate = watch('baseDate')
  const userMappings = watch('userMappings')
  const taskKindMappings = watch('taskKindMappings')

  const [templates, setTemplates] = useState<TaskTemplateListItem[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  )
  const [templateTasks, setTemplateTasks] = useState<TemplateTaskViewModel[]>(
    []
  )
  const [mappingRequiredAssignToUsers, setMappingRequiredAssignToUsers] =
    useState<UserRef[]>([])
  const [mappingRequiredTaskKinds, setMappingRequiredTaskKinds] = useState<
    TaskKindRef[]
  >([])

  const userDataSource = useMemberDataSource(
    EntityType.Project,
    projectId,
    true
  )
  const taskKindDataSource = useTaskKindDataSource(
    EntityType.Project,
    projectId,
    false,
    false,
    false
  )

  // マッピングエラーの有無
  const hasMappingError = useMemo(() => {
    const userMissing = mappingRequiredAssignToUsers.some(
      (user) => !userMappings?.[user.id]
    )
    const kindMissing = mappingRequiredTaskKinds.some(
      (kind) => !taskKindMappings?.[kind.id]
    )
    return userMissing || kindMissing
  }, [
    mappingRequiredAssignToUsers,
    mappingRequiredTaskKinds,
    taskKindMappings,
    userMappings,
  ])

  // タスクテンプレート一覧の読み込み
  const loadTaskTemplates = useCallback(
    async (searchKeyword: string, sort: string[]) => {
      try {
        const result = await lazyGetTaskTemplatesQuery({
          projectId,
          scope: TaskTemplateScope.Public,
          keyword: searchKeyword,
          sort: sort.length ? sort : undefined,
        }).unwrap()

        if (result?.taskTemplates) {
          const list = result.taskTemplates.map<TaskTemplateListItem>(
            (template) => ({
              id: template.id,
              name: template.name,
              scope: template.scope,
              updatedAt: template.updatedAt,
              updatedBy: template.updatedBy,
            })
          )

          setTemplates(list)
          if (list.length > 0) {
            const nextSelected = list.find(
              (t) => t.id === selectedTemplateId
            )?.id
            setSelectedTemplateId(nextSelected ?? null)
          }
        }
      } catch (error) {
        showApiErrors(error)
      }
    },
    [lazyGetTaskTemplatesQuery, projectId, selectedTemplateId, showApiErrors]
  )

  // フォームリセット
  const resetForm = useCallback(() => {
    reset({
      searchKeyword: '',
      baseDate: new Date(),
      userMappings: {},
      taskKindMappings: {},
    })
    setTemplates([])
    setSelectedTemplateId(null)
    setTemplateTasks([])
    setMappingRequiredAssignToUsers([])
    setMappingRequiredTaskKinds([])
  }, [reset])

  // 選択されたテンプレートのタスク読み込み
  const loadTaskTemplateTasks = useCallback(
    async (templateId: string) => {
      try {
        const result = await lazyLoadTaskTemplateQuery({
          taskTemplateId: templateId,
          projectId,
        }).unwrap()

        // テンプレートタスクの表示用データを構築してセット
        setTemplateTasks(buildTaskView(result.taskTemplateTasks))

        // マッピング必須データのセットアップ
        setMappingRequiredAssignToUsers(
          result.mappingRequiredAssignToUsers ?? []
        )
        setMappingRequiredTaskKinds(result.mappingRequiredTaskKinds ?? [])

        // マッピング用のデフォルト値(null)をセット
        const userMappingDefaults: Record<string, string | null> = {}
        result.mappingRequiredAssignToUsers.forEach((user) => {
          userMappingDefaults[user.id] = null
        })
        const taskKindDefaults: Record<string, string | null> = {}
        result.mappingRequiredTaskKinds.forEach((kind) => {
          taskKindDefaults[kind.id] = null
        })

        setValue('userMappings', userMappingDefaults)
        setValue('taskKindMappings', taskKindDefaults)
      } catch (error) {
        showApiErrors(error)
      }
    },
    [lazyLoadTaskTemplateQuery, projectId, setValue, showApiErrors]
  )

  // テンプレートからタスク登録を実行
  const insertTasksByTemplate = useCallback(async () => {
    // 選択されたテンプレートがない場合は処理しない
    if (!selectedTemplateId) {
      return
    }

    // taskKindMappingsのvalueにnullが含まれている場合は正しく処理できないため、処理しない
    if (Object.values(taskKindMappings).some((v) => v === null)) {
      toast.error(t('message.taskTemplate.requiredCopyDestination'))
      return
    }

    try {
      // 担当者のマッピング情報をもとにリクエストパラメータ用のデータを構築
      let assigneeMap: AssigneeMap[] | undefined = undefined
      if (Object.keys(userMappings).length > 0) {
        assigneeMap = Object.entries(userMappings).map<AssigneeMap>(
          ([fromUserId, toUserId]) => ({
            fromUserId: fromUserId,
            toUserId: toUserId ?? undefined,
          })
        )
      }
      // タスク種別のマッピング情報をもとにリクエストパラメータ用のデータを構築
      let taskKindMap: TaskKindMap[] | undefined = undefined
      if (Object.keys(taskKindMappings).length > 0) {
        taskKindMap = Object.entries(taskKindMappings).reduce(
          (acc, [fromTaskKindId, toTaskKindId]) => {
            if (toTaskKindId) {
              acc.push({
                fromTaskKindId: fromTaskKindId,
                toTaskKindId: toTaskKindId,
              })
            }
            return acc
          },
          [] as TaskKindMap[]
        )
      }

      // テンプレートからタスク登録APIを実行
      const params: InsertTasksFromTemplateRequest = {
        projectId: projectId,
        taskTemplateId: selectedTemplateId!,
        startDate: dayjs(baseDate).format(JsonDateFormat.YYYYMMDD),
        assigneeMap: assigneeMap,
        taskKindMap: taskKindMap,
      }
      await insertTasksFromTemplateMutation(params).unwrap()
      const objectEventMessage: ObjectEventMessage<Task> = {
        eventType: NotifyEventType.Inserted,
        id: '', // 複数登録されるためIDはダミーの空文字を設定
        object: undefined,
      }
      dispatch(notifyTaskEvent(objectEventMessage))

      // 登録完了後の処理
      onSubmit()

      toast.success(t('message.task.taskRegistered'))
    } catch (error) {
      showApiErrors(error)
    }
  }, [
    baseDate,
    dispatch,
    insertTasksFromTemplateMutation,
    onSubmit,
    projectId,
    selectedTemplateId,
    showApiErrors,
    t,
    taskKindMappings,
    toast,
    userMappings,
  ])

  const canRegister = useMemo(() => {
    return templateTasks.length > 0 && !isRegistering
  }, [isRegistering, templateTasks.length])

  // 初回ロード処理
  useEffectOnce(() => {
    if (!isOpen) {
      resetForm()
      return
    }
    loadTaskTemplates('', [])
  })

  return {
    control,
    setValue,
    getValues,
    templates,
    templateTasks,
    selectedTemplateId,
    setSelectedTemplateId,
    mappingRequiredAssignToUsers,
    mappingRequiredTaskKinds,
    userDataSource,
    taskKindDataSource,
    insertTasksByTemplate,
    loadTaskTemplates,
    loadTaskTemplateTasks,
    canRegister,
    hasMappingError,
    isRegistering,
  }
}
